import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PaymentSuccessPage — polls /api/payments/status/{session_id} until paid.
 * Renders the human-sentence receipt letter, not just JSON.
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!sid) return;
    const poll = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/payments/status/${sid}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setStatus(j);
        setAttempts((a) => a + 1);
        if (j.payment_status === "paid" || j.payment_status === "failed" || j.payment_status === "expired") {
          setDone(true);
          return;
        }
        if (attempts < 15) {
          timer.current = setTimeout(poll, 2000);
        }
      } catch (e) {
        setStatus({ error: e.message });
        setDone(true);
      }
    };
    poll();
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line
  }, [sid]);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative">
      <TopNav />
      <div className="mx-auto max-w-3xl px-4 md:px-8 py-16" data-testid="payment-success">
        <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">Membership · receipt</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase text-sage-text mb-6">
          {status?.payment_status === "paid" ? "Paid. Landed on the Ledger." :
           status?.payment_status === "failed" ? "Charge failed." :
           status?.payment_status === "expired" ? "Session expired." :
           done ? "Something went sideways." : "Confirming…"}
        </h1>

        {status?.letter && (
          <div className="border-l-2 border-sage-accent pl-4 mb-8 text-base text-sage-muted leading-relaxed sage-prose" data-testid="payment-letter">
            {status.letter}
          </div>
        )}

        {status && (
          <div className="border border-sage-border p-6 mb-8 font-mono text-xs text-sage-muted space-y-1" data-testid="payment-details">
            <div className="flex justify-between"><span className="text-sage-faint">session</span><span className="tabular-nums">{sid?.slice(0, 24)}…</span></div>
            <div className="flex justify-between"><span className="text-sage-faint">tier</span><span>{status.lookup_key || "—"}</span></div>
            <div className="flex justify-between"><span className="text-sage-faint">status</span><span>{status.status || "—"}</span></div>
            <div className="flex justify-between"><span className="text-sage-faint">payment</span><span>{status.payment_status || "—"}</span></div>
            {status.amount_total != null && (
              <div className="flex justify-between"><span className="text-sage-faint">amount</span><span className="tabular-nums">${(status.amount_total/100).toFixed(2)}</span></div>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Link
            to="/sage-ledger"
            data-testid="payment-ledger-link"
            className="text-xs uppercase tracking-[0.1em] border border-sage-accent text-sage-accent px-4 py-2 hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150"
          >
            See the public Ledger →
          </Link>
          <Link
            to="/tiers"
            data-testid="payment-tiers-link"
            className="text-xs uppercase tracking-[0.1em] border border-sage-border text-sage-muted px-4 py-2 hover:border-sage-text hover:text-sage-text transition-colors duration-150"
          >
            Back to Membership
          </Link>
        </div>
      </div>
    </div>
  );
}
